import { SearchSettings } from '@givebacks/api/query';
import { createColumnHelper } from '@tanstack/react-table';

import { columnsCreator } from '../../../src/utils';
import { createSearchFields } from '../Search/search.utils';
import { Table } from '../Table';
import { TableContext } from '../types/types';
import { createActionsColumn } from './createActionsColumn';
import { createExportOptions } from './createExportOptions';
import { createPrintColumn } from './createPrintColumn';
import { createRowSelectionColumn } from './createRowSelectionColumn';
import { CreateTableMeta } from './createTable.types';

type CreateTable<
  TData,
  TSearch extends SearchSettings = SearchSettings,
  TMeta extends Record<string, unknown> = Record<string, unknown>,
  TTable extends typeof Table<TData, TMeta> = typeof Table<TData, TMeta>,
  TReactTable = CreateTableMeta<TData, TMeta>,
> = {
  createExportOptions: typeof createExportOptions<TData>;

  rowSelectionColumn: ReturnType<typeof createRowSelectionColumn<TData>>;

  actionsColumn: ReturnType<typeof createActionsColumn<TData>>;

  printColumn: ReturnType<typeof createPrintColumn<TData>>;
  /** Helps create Table Columns */
  columnHelper: ReturnType<typeof createColumnHelper<TData>>;

  /** Helps to create export columns */
  columnExportHelper: ReturnType<typeof columnsCreator<TData>>;

  /** Creates search fields */
  createSearchFields: typeof createSearchFields<TSearch>;

  /**
   * Core Table Component with all types pre assigned
   */
  Table: TTable;
  useContext: () => TableContext<TData, TReactTable, TSearch>;

  /** @deprecated - use columnsExportCreator */
  columnsCreator: ReturnType<typeof columnsCreator<TData>>;
};

/**
 * Used to create a table with the necessary utilities
 * and returns them when the correct typing provided
 */
function createTable<
  TData,
  TSearch extends SearchSettings = SearchSettings,
  TMeta extends Record<string, unknown> = Record<string, unknown>,
  TTable extends typeof Table<TData, TMeta> = typeof Table<TData, TMeta>,
>(): CreateTable<TData, TSearch, TMeta, TTable> {
  const rowSelectionColumn = createRowSelectionColumn<TData>();
  const columnHelper = createColumnHelper<TData>();
  const _columnsCreator = columnsCreator<TData>();
  const _createSearchFields = createSearchFields<TSearch>;
  const _printColumn = createPrintColumn<TData>();
  const _actionsColumn = createActionsColumn<TData>();
  return {
    rowSelectionColumn,
    actionsColumn: _actionsColumn,
    printColumn: _printColumn,
    columnHelper,
    createExportOptions,
    columnExportHelper: _columnsCreator,
    createSearchFields: _createSearchFields,
    columnsCreator: _columnsCreator,
    useContext: Table.useContext,
    Table: Table,
  } as unknown as CreateTable<TData, TSearch, TMeta, TTable>;
}

export { createTable };
